import { useRouter }           from "next/router";
import { useEffect, useState } from "react";
import useSWRInfinite          from "swr/infinite";

import { useGetProfileId, useGetProfileName }              from "_common/hooks/auth";
import Toast                                               from "_common/toast";
import { isPageRoute }                                     from "_common/utils";
import { combineArraysFromObjects }                        from "_common/utils";
import { useGetDonationGoal as useGetDonationGoalFromIVS } from "_components/features/ivsChat/states";
import { useGetDonationGoal, useSetDonationGoal }          from "_components/pages/studio/states";
import { useGetUpdateSession }                             from "_jotai/liveSession";
import { TopDonorLeaderboardAPI, donationGoalMutationAPI } from "_services/api/broadcast";
import { getDonationsList }                                from "_services/firebase/broadcast";

import Presentation from "./presentation";

const fetchDonorsLeaderBoard = async ( { broadcastId, pageNo } ) => {
	try {
		const response = await TopDonorLeaderboardAPI ( { broadcastSessionId: broadcastId, pageNo, pageSize: 10 } );

		return response;
	} catch ( error ) {
		Toast.error ( error.displayMsg );
	}
};

const StudioDonationTab = ( {
	isBlockedByStreamer,
	chatPoppedOut,
	isSessionEnded,
	broadcastId
} ) => {

	const router = useRouter ();

	const isStudioPage = isPageRoute ( { router, page: "studio" } );

	const sessionData                = useGetUpdateSession ();
	const userProfileName            = useGetProfileName ();
	const userProfileId              = useGetProfileId ();
	const setDonationGoal            = useSetDonationGoal ();
	const donationGoalFromLiveParams = useGetDonationGoal ()?.donationGoalAmount;
	const donationGoalFromSocket     = useGetDonationGoalFromIVS ();

	const isSessionLive = sessionData && !sessionData.endTimeUTC;
	const isStreamer    = sessionData?.broadcaster?.sportsFan.id === userProfileId || isStudioPage;
	const streamerName  = ( isStudioPage ) ? userProfileName : sessionData?.broadcaster?.sportsFan?.name;

	// if user is on the studio page, then we use the userProfileName as the streamerName as sessionData is not available yet.
	const donationGoalFromSessionData = donationGoalFromSocket || sessionData?.donationGoal;
	const donationAmount              = isStreamer ? donationGoalFromLiveParams : donationGoalFromSessionData;

	const { data, error, size, setSize } = useSWRInfinite (
		broadcastId ? index => {
			return { key: "donors-leaderboard", pageNo: index + 1, broadcastId };
		} : null,
		fetchDonorsLeaderBoard,
		{
			revalidateOnFocus   : false,
			revalidateFirstPage : false
		}
	);

	const totalDonationReceived = data?.length ? data[0]?.totalDonation : 0;
	const isLoadingInitialData  = !data && !error;
	const isDepleted            = data?.[size - 1]?.leaderboard?.length === 0;
	const isLoadingMore         = isLoadingInitialData || ( size > 0 && data && typeof data[size - 1] === "undefined" );
	const __data                = combineArraysFromObjects ( data || [], "leaderboard" );

	const [ showDonationGoal, setShowDonationGoal ]         = useState ( false );
	const [ donationGoalToggle, setDonationGoalToggle ]     = useState ( { status: false } );
	const [ showLeaderBoard, setShowLeaderBoard ]           = useState ( false );
	const [ donationCommentsList, setDonationCommentsList ] = useState ( [] );

	let totalRetries   = 10;
	let currentRetries = 0;
	let __donationsList;

	useEffect ( () => {
		if ( !broadcastId )
			return;
		init ();

		return () => {
			clearListeners ();
		};
	}, [ broadcastId ] );

	useEffect ( () => {
		setShowLeaderBoard ( !isStreamer );
	}, [ isStreamer ] );

	const init = async () => {
		currentRetries  = 0;
		__donationsList = await donationsCommentsListListener ();

	};

	const clearListeners = () => {
		if ( __donationsList )
			__donationsList ();

	};

	const donationsCommentsListListener = async () => {
		return await getDonationsList ( broadcastId, async ( donations, error ) => {

			if ( error && error?.code === "permission-denied" ) {
				currentRetries ++;

				if ( currentRetries > totalRetries )
					return;

				await donationsCommentsListListener ();

				return;
			}

			donations.docChanges ().forEach ( ele => {
				const data = ele.doc.data ();

				updateDonationComments ( { comment: data, type: ele.type } );
			} );
		} );
	};

	const updateDonationComments = ( { comment, type } ) => {
		if ( type === "added" ) {
			setDonationCommentsList ( prevState => [ ...prevState, comment ] );

		} else if ( type === "modified" ) {

			setDonationCommentsList ( prevState => {
				const updatedCommentIndex = prevState.findIndex ( el => el.id === comment.id );
				const newArr              = [ ...prevState ];

				newArr.splice ( updatedCommentIndex, 1, comment );

				return newArr;
			} );

		} else {
			// type === 'removed'
			setDonationCommentsList ( prevState => [ ...prevState ].filter ( el => el.id === comment.id ) );
		}
	};

	const handleDonationGoalToggle = async data => {
		setDonationGoalToggle ( { status: data.status } );
		setShowDonationGoal ( data.status ? true : false );

		// setAnchorEl ( data.e.target );
	};

	const saveAndEnableDonationGoal = async goalAmount => {
		goalAmount = parseInt ( goalAmount );

		if ( isSessionLive && broadcastId ) {
			try {
				await donationGoalMutationAPI ( { sessionId: broadcastId, donationGoal: goalAmount } );

				setDonationGoal ( { donationGoalStatus: !!goalAmount, donationGoalAmount: goalAmount } );
				Toast.info ( "Donation Goal updated successfully." );
			} catch ( error ) {
				Toast.error ( error.displayMsg );
			}
		}

		setDonationGoalToggle ( { status: true } );
		setShowDonationGoal ( false );

		setDonationGoal ( {
			donationGoalAmount : goalAmount,
			donationGoalStatus : !!goalAmount
		} );

	};

	const closeDonationGoalModal = () => {
		setShowDonationGoal ( false );
		setDonationGoalToggle ( { status: false } );
	};

	const toggleLeaderboardHandler = () => {
		setShowLeaderBoard ( prevState => !prevState );
	};

	const loadMore = () => setSize ( size + 1 );

	return (
		<Presentation
			broadcastId = { broadcastId }
			chatPoppedOut = { chatPoppedOut }
			closeDonationGoalModal = { closeDonationGoalModal }
			commentsList = { donationCommentsList }
			donationGoalAmount = { donationAmount }
			donationGoalToggle = { donationGoalToggle }
			handleDonationGoalToggle = { handleDonationGoalToggle }
			isBlockedByStreamer = { isBlockedByStreamer }
			isDepleted = { isDepleted }
			isFetching = { isLoadingMore }
			isSessionEnded = { isSessionEnded }
			isStreamer = { isStreamer }
			loadMore = { loadMore }
			saveAndEnableDonationGoal = { saveAndEnableDonationGoal }
			showDonationGoal = { showDonationGoal }
			showLeaderBoard = { showLeaderBoard }
			streamerName = { streamerName }
			toggleLeaderboardHandler = { toggleLeaderboardHandler }
			totalDonationReceived = { totalDonationReceived }
			usersList = { __data }
		/>
	);
};

export default StudioDonationTab;
